import { useSearch } from './useSearch'
import { Drawer } from '@lounge-fe/ui-kit'
import { Search } from '../Search'
import { ShopifySectionComponent } from '@/types/ShopifySectionComponent'
import { HeaderData } from './types'
import { Basket } from '../Basket'
import { StandardProductCard } from '@/features/product/components/StandardProductCard'
import {
  ClickedProductAfterSearchEvent,
  ConvertedObjectIDsAfterSearchEvent,
  useSearchInsights,
} from '@/hooks/useSearchInsights'
import { useAuth } from '@lounge-fe/auth'
import { Navigation } from '@/features/navigation/components/Navigation'
import { useMemo } from 'react'
import { NavigationDrawer } from '@/features/navigation/components/NavigationDrawer'
import { HomeNavigation } from '@/features/navigation/components/NavigationDrawer/HomeNavigation'
import { SubNavigation } from '@/features/navigation/components/NavigationDrawer/SubNavigation'

export const Header: ShopifySectionComponent<HeaderData> = ({ data }) => {
  const { status: authStatus } = useAuth()

  const searchTiles = (data?.searchTiles ?? []).filter(({ image }) =>
    Boolean(image),
  )

  const menuItems = useMemo(() => data?.menuItems ?? [], [data?.menuItems])
  const drawerFooterMenu = data?.drawerFooterMenu
  const emptyBagAction = data?.emptyBagAction

  const {
    onSearchChange,
    isSearchOpen,
    query,
    hits,
    isLastPage,
    showMore,
    nbHits,
    suggestedSearchTerms,
  } = useSearch({
    suggestedSearchTermFallbacks: data?.suggestedSearchTermFallbacks ?? [],
  })

  const { clickedProductAfterSearch, convertedObjectIDsAfterSearch } =
    useSearchInsights()

  return (
    <Navigation
      basketComponent={
        <Basket
          emptyBagAction={emptyBagAction}
          choiceChipQuickAdd={data?.cartChoiceChipQuickAdd}
          cartDiscountProgress={data?.cartDiscountProgress}
        />
      }
      searchComponent={
        <Search
          isFull={query.length > 0}
          isOpen={isSearchOpen}
          className="py-2 xl:pt-0"
        >
          {query.length > 0 && (
            <>
              <Search.Stats query={query} count={nbHits} />
              <Search.Grid>
                {hits.map((hit, idx) => (
                  <StandardProductCard
                    product={hit}
                    key={hit.id}
                    useImageCarousel
                    useQuickAdd
                    onClick={() => {
                      if (hit.objectID) {
                        clickedProductAfterSearch({
                          objectID: hit.objectID,
                          eventName: ClickedProductAfterSearchEvent.Search,
                          queryID: hit.queryID,
                          position: idx + 1,
                        })
                      }
                    }}
                    onQuickAdd={() => {
                      if (hit.queryID && hit.objectID) {
                        convertedObjectIDsAfterSearch({
                          objectID: hit.objectID,
                          eventName: ConvertedObjectIDsAfterSearchEvent.Search,
                          queryID: hit.queryID,
                        })
                      }
                    }}
                    showWishlist={authStatus === 'authenticated' ? true : false}
                  />
                ))}
              </Search.Grid>
              {!isLastPage && (
                <Search.LoadMore
                  currentHits={hits.length}
                  totalHits={nbHits}
                  onClick={() => showMore()}
                />
              )}
            </>
          )}
          {query.length === 0 && (
            <Search.Menu
              tiles={searchTiles.map((tile) => ({
                href: tile.link,
                image: tile.image,
                title: tile.title,
              }))}
            >
              <Search.Recommendations
                suggestedSearchTerms={suggestedSearchTerms}
              />
            </Search.Menu>
          )}
        </Search>
      }
      onSearchSubmit={(value) => onSearchChange(value)}
    >
      <NavigationDrawer.Drawer>
        {({ onOpen }) => (
          <>
            <Navigation.Header onMenuClick={onOpen} />
            <Drawer.Pagination>
              <NavigationDrawer.Content>
                <HomeNavigation
                  menuItems={menuItems}
                  drawerFooterMenu={drawerFooterMenu}
                />
                <SubNavigation menuItems={menuItems} tiles={data?.tiles} />
              </NavigationDrawer.Content>
            </Drawer.Pagination>
          </>
        )}
      </NavigationDrawer.Drawer>
    </Navigation>
  )
}
